import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
// Type-only, fully erased at runtime: it exists to pull in @gjl/analytics's
// ambient `Window.gtag` declaration. Declaring one locally would conflict with
// it wherever both are in the compile (merged global types must be identical).
import type {} from '@gjl/analytics'

/**
 * The GA4 engagement events every fuguelab.com page emits.
 *
 * These live with the chrome rather than in either app because both bundles
 * report into the SAME GA4 property (527738893) — one domain, one visitor
 * journey, and the funnel this whole hymnal project exists to measure runs
 * across the boundary between them. Two copies of the hook would eventually
 * send two shapes of `page_engagement`, and the segment comparing hymnal
 * readers to marketing-site readers would quietly stop being a comparison.
 */

/**
 * Straight to `window.gtag`, NOT through `@gjl/analytics`'s GA4 singleton.
 *
 * That singleton only records once `initGA4()` has run, and studio/client never
 * calls it — it still uses the inline gtag snippet in its `index.html`, which
 * `scripts/ci/ga4-guard.mjs` grandfathers as a LEGACY_FORK. Routing these
 * through the singleton would therefore no-op on the marketing site and take
 * `page_engagement` and `return_visit` off every page of it, silently. The
 * global is the one surface both bundles genuinely share.
 */
function track(eventName: string, params: Record<string, unknown>) {
  window.gtag?.('event', eventName, params)
}

/**
 * Tracks scroll depth and time on page, fires a page_engagement GA4 event
 * when the user navigates away or hides the tab.
 */
export function usePageEngagement() {
  const location = useLocation()
  // Init to 0 and set in effect — calling performance.now() during render
  // violates React's purity rule (caught by react-hooks/purity lint).
  const startTime = useRef(0)
  const maxScroll = useRef(0)
  const fired = useRef(false)

  useEffect(() => {
    // Reset on route change
    startTime.current = performance.now()
    maxScroll.current = 0
    fired.current = false

    function updateScrollDepth() {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      if (docHeight > 0) {
        const percent = Math.min(Math.round((scrollTop / docHeight) * 100), 100)
        if (percent > maxScroll.current) {
          maxScroll.current = percent
        }
      }
    }

    function fireEvent() {
      if (fired.current) return
      fired.current = true
      const timeOnPage = (performance.now() - startTime.current) / 1000
      track('page_engagement', {
        event_category: 'engagement',
        page_path: location.pathname,
        time_on_page_seconds: Math.round(timeOnPage),
        scroll_depth_percent: maxScroll.current,
      })
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        fireEvent()
      }
    }

    window.addEventListener('scroll', updateScrollDepth, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', fireEvent)

    return () => {
      fireEvent()
      window.removeEventListener('scroll', updateScrollDepth)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', fireEvent)
    }
  }, [location.pathname])
}

const VISIT_COUNT_KEY = 'fuguelab_visit_count'

/**
 * Tracks return visits via localStorage counter.
 * Fires a return_visit GA4 event on visit 2+.
 *
 * The counter key is shared with the marketing site on purpose: a reader who
 * arrives on a hymn page and comes back a week later to the homepage is the
 * same returning visitor, and localStorage is per-origin, so both builds see
 * the one count.
 */
export function useReturnVisitTracker() {
  useEffect(() => {
    try {
      const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) ?? '0', 10) + 1
      localStorage.setItem(VISIT_COUNT_KEY, String(count))
      if (count > 1) {
        track('return_visit', {
          event_category: 'engagement',
          visit_number: count,
        })
      }
    } catch {
      // localStorage unavailable (private browsing, etc.)
    }
  }, [])
}
